import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import axios from 'axios';
import { MessageSquare, FileText, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { API_URL, fmtTime } from './chatShared';

// Escolher ficheiros enviados no chat para juntar ao contrato (copiados ao guardar)
export const ChatFilesPicker = forwardRef(({ contractId, initialFileId }, ref) => {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState(null);
  const [sel, setSel] = useState([]);

  useEffect(() => {
    if (!initialFileId) return;
    axios.get(`${API_URL}/chat/my-files`, { params: { file_id: initialFileId } }).then((r) => {
      const f = r.data[0];
      if (f) setSel((s) => (s.some((x) => x.id === f.id) ? s : [...s, f]));
    }).catch(() => {});
  }, [initialFileId]);

  const attach = async (cid, ids) => {
    if (!ids.length) return 0;
    const { data } = await axios.post(`${API_URL}/contracts/${cid}/attach-chat-files`, { file_ids: ids });
    return data.attached;
  };
  useImperativeHandle(ref, () => ({ hasSelected: () => sel.length > 0, attachTo: (cid) => attach(cid, sel.map((f) => f.id)) }));

  const openPicker = () => {
    setOpen(true);
    axios.get(`${API_URL}/chat/my-files`, { params: { days: 30 } }).then((r) => setFiles(r.data)).catch(() => setFiles([]));
  };
  const toggle = (f) => setSel((s) => (s.some((x) => x.id === f.id) ? s.filter((x) => x.id !== f.id) : [...s, f]));
  const confirm = async () => {
    if (contractId) { await attach(contractId, sel.map((f) => f.id)); setSel([]); }
    setOpen(false);
  };

  return (
    <div className="mt-3" data-testid="chat-files-picker">
      <Button type="button" variant="outline" size="sm" onClick={openPicker} className="gap-1.5" data-testid="chat-files-open">
        <MessageSquare className="w-4 h-4" />Juntar ficheiro do chat
      </Button>
      {sel.length > 0 && !contractId && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {sel.map((f) => (
            <span key={f.id} className="text-xs bg-sky-50 text-sky-800 rounded-full px-2 py-1 flex items-center gap-1" data-testid={`chat-file-selected-${f.id}`}>
              <FileText className="w-3 h-3" />{f.filename}<button type="button" onClick={() => toggle(f)}><X className="w-3 h-3" /></button>
            </span>
          ))}
          <span className="text-[11px] text-slate-500 w-full">Serão copiados para o contrato quando o guardar.</span>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="chat-files-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Ficheiros do chat (últimos 30 dias)</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100 border border-slate-100 rounded-lg">
            {!files && <p className="p-3 text-sm text-slate-400">A carregar…</p>}
            {files?.length === 0 && <p className="p-3 text-sm text-slate-400">Sem ficheiros nas suas conversas.</p>}
            {files?.map((f) => (
              <label key={f.id} className="flex items-start gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`chat-file-${f.id}`}>
                <input type="checkbox" checked={sel.some((x) => x.id === f.id)} onChange={() => toggle(f)} className="mt-1" />
                <span className="min-w-0">
                  <span className="block truncate font-medium text-slate-800">{f.filename}</span>
                  <span className="block text-xs text-slate-500">{f.conversa} · {f.enviado_por} · {fmtTime(f.created_at)}</span>
                </span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={confirm} disabled={!sel.length} className="bg-[#2D3E6F] hover:bg-[#24335c]" data-testid="chat-files-confirm">Juntar {sel.length || ''}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});
ChatFilesPicker.displayName = 'ChatFilesPicker';
